import React, { useCallback, useEffect, useState } from "react";
import { Box, IconButton, Stack } from "@mui/material";
import { Edit, Add, CloudUpload } from "@mui/icons-material";
import { ColorsConstants } from "../../cssUtils/constants";
import AppTable from "../../common/app-table";
import TableSearchInput from "../TableSearchInput";
import ClientDetailView from "./ClientDetailView";
import ClientFormDialog from "./ClientFormDialog";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import { useAuth } from "../../hooks/AuthProvider";
import { MFD_CLIENTS_ENDPOINT } from "../../config/urls";

const normalizeClient = (c) => ({
  ...c,
  client_name:  c.name,
  phone_number: c.phone_no,
});

const Clients = ({ onNavigate }) => {
  const { getValidToken } = useAuth();
  const [clients, setClients]           = useState([]);
  const [page, setPage]                 = useState(0);
  const [rowsPerPage, setRowsPerPage]   = useState(20);
  const [totalCount, setTotalCount]     = useState(0);
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [search, setSearch]             = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [isModalOpen, setIsModalOpen]   = useState(false);
  const [editData, setEditData]         = useState(null);

  const getHeaders = useCallback(async () => {
    const token  = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  }, [getValidToken]);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: rowsPerPage, offset: page * rowsPerPage });
    if (search.trim()) params.set("search", search.trim());

    try {
      const headers = await getHeaders();
      const res  = await fetch(`${MFD_CLIENTS_ENDPOINT}?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to fetch clients");
      const data = await res.json();
      setClients((data.results ?? []).map(normalizeClient));
      setTotalCount(data.count ?? 0);
    } catch (err) {
      console.error("Error fetching clients:", err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, getHeaders]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleView = (row) => setSelectedClient(row);
  const handleAdd  = ()    => { setEditData(null); setIsModalOpen(true); };
  const handleEdit = (row) => { setEditData(row);  setIsModalOpen(true); };

  const handleSave = async (form) => {
    const url    = editData ? `${MFD_CLIENTS_ENDPOINT}${editData.id}/` : MFD_CLIENTS_ENDPOINT;
    const method = editData ? "PATCH" : "POST";
    setSaving(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (!res.ok) throw new Error("Failed to save client");
      setIsModalOpen(false);
      fetchClients();
    } catch (err) {
      console.error("Error saving client:", err);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { id: "pan",          label: "PAN",          minWidth: 130 },
    { id: "client_code",  label: "Client Code",  minWidth: 120 },
    { id: "client_name",  label: "Client Name",  minWidth: 180 },
    { id: "country_code", label: "Country Code", minWidth: 110 },
    { id: "phone_number", label: "Phone Number", minWidth: 140 },
    { id: "broker_code",  label: "Broker Code",  minWidth: 130 },
    { id: "dob",          label: "Date of Birth", minWidth: 130 },
    { id: "source",       label: "Source",       minWidth: 120 },
    {
      id: "actions",
      label: "Actions",
      minWidth: 80,
      render: (row) => (
        <Stack direction="row">
          <IconButton
            size="medium"
            sx={{ color: ColorsConstants.primaryBlue100 }}
            onClick={() => handleEdit(row)}
          >
            <Edit fontSize="medium" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  if (selectedClient) {
    return (
      <ClientDetailView
        client={selectedClient}
        onBack={() => setSelectedClient(null)}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ flex: 1, maxWidth: 450 }}>
          <TableSearchInput
            searchQuery={search}
            onSearchChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search by PAN, client code, client name, phone number"
          />
        </Box>
        <Stack direction="row" spacing={1.5}>
          <ButtonTwo
            startIcon={<CloudUpload />}
            onClick={() => onNavigate?.("mutual_fund_distributor.file_upload", { type: "clients" })}
          >
            Upload
          </ButtonTwo>
          <ButtonOne startIcon={<Add />} onClick={handleAdd}>
            Add Client
          </ButtonOne>
        </Stack>
      </Box>

      <AppTable
        columns={columns}
        rows={clients}
        rowKey="id"
        loading={loading}
        emptyMessage="No clients found"
        maxHeight={620}
        hideMainScroll={false}
        containerSx={{ overflowX: "auto" }}
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        onRowClick={handleView}
      />

      <ClientFormDialog
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        editData={editData}
        saving={saving}
      />
    </Box>
  );
};

export default Clients;
